import { useState } from "react";

import { IconCheck, IconPencil, IconTrash } from "@tabler/icons";
import { FieldArray, useFormikContext } from "formik";
import { useTheme } from "styled-components";

import { FormikTextInput } from "@sso/components/Formik";
import {
  LicenceType,
  PricingFormModel,
  PricingRuleCreateRequest,
  PricingRuleKeys,
  SoftwareLicenceFormValues,
} from "@sso/types";
import { Button } from "@sso/ui/Button";
import { Flex } from "@sso/ui/FlexBox";
import { IconButton } from "@sso/ui/IconButton";
import { Typography } from "@sso/ui/Typography";

interface PricingRulesRangeFormProps {
  rules: PricingRuleCreateRequest[];
  licenceType: LicenceType;
  fieldAccessor: string;
  errors: any | null | undefined;
}

interface PricingRuleActionButtonsProps {
  index: number;
  allowEdit: boolean;
  editMode: boolean;
  onEditSelect: (index: number) => void;
  onEditComplete: () => void;
  onDelete: (index: number) => void;
}

const PricingRulesActionButtons = ({
  index,
  allowEdit,
  editMode,
  onEditSelect,
  onEditComplete,
  onDelete,
}: PricingRuleActionButtonsProps) => {
  const theme = useTheme();

  return (
    <>
      {allowEdit && (
        <>
          {editMode ? (
            <IconButton
              color={theme.colors.success.main}
              hoverColor={theme.colors.success.main}
              onClick={() => onEditComplete()}
            >
              <IconCheck />
            </IconButton>
          ) : (
            <IconButton
              color={theme.colors.grey[700]}
              hoverColor={theme.colors.info.main}
              onClick={() => onEditSelect(index)}
            >
              <IconPencil />
            </IconButton>
          )}
        </>
      )}

      <IconButton color={theme.colors.grey[700]} hoverColor={theme.colors.error.main} onClick={() => onDelete(index)}>
        <IconTrash />
      </IconButton>
    </>
  );
};

const PricingRulesRangeForm = ({ fieldAccessor, rules, licenceType, errors }: PricingRulesRangeFormProps) => {
  const theme = useTheme();
  const { setFieldValue, setFieldTouched, validateForm } = useFormikContext<
    SoftwareLicenceFormValues | PricingFormModel
  >();
  const [editIndex, setEditIndex] = useState<number | null>(null);

  const deleteRule = (index: number) => {
    const newRules = [...rules];
    var rule = rules[index];
    const lastBeforeRemoved = rules.length - 1;

    const filtered = newRules.filter((_, i) => i !== index);

    if (!rule.isLast && index !== lastBeforeRemoved) {
      const lastAfterRemoved = filtered.length - 1;
      adjustRuleRanges(filtered, index, rule[PricingRuleKeys.MIN_RANGE]);
      setFieldValue(fieldAccessor, filtered);
      setFieldValue(`${fieldAccessor}[${lastAfterRemoved}].${PricingRuleKeys.MAX_RANGE}`, "");
      setFieldValue(`${fieldAccessor}[${lastAfterRemoved}].${PricingRuleKeys.PRICE}`, "");
      setFieldTouched(`${fieldAccessor}[${lastAfterRemoved}].${PricingRuleKeys.MAX_RANGE}`, false);
      setFieldTouched(`${fieldAccessor}[${lastAfterRemoved}].${PricingRuleKeys.PRICE}`, false);
      setEditIndex(null);
    } else {
      setFieldValue(fieldAccessor, filtered);
    }
  };

  const adjustRuleRanges = (rules: PricingRuleCreateRequest[], index: number, minRange?: number) => {
    for (let i = index; i < rules.length; i++) {
      const element = rules[i];
      element[PricingRuleKeys.MIN_RANGE] = minRange;

      if (element[PricingRuleKeys.MIN_RANGE]! > element[PricingRuleKeys.MAX_RANGE]!) {
        element[PricingRuleKeys.MAX_RANGE] = element[PricingRuleKeys.MIN_RANGE]!;
      }
      minRange = element[PricingRuleKeys.MAX_RANGE]! + 1;
    }
  };

  const onEditComplete = () => {
    if (editIndex == null) {
      return;
    }

    const ruleToEdit = rules[editIndex];

    if (
      !ruleToEdit ||
      !ruleToEdit[PricingRuleKeys.MAX_RANGE] ||
      !ruleToEdit[PricingRuleKeys.PRICE || !ruleToEdit[PricingRuleKeys.MIN_RANGE]]
    ) {
      return;
    }

    if (ruleToEdit[PricingRuleKeys.MAX_RANGE]! < ruleToEdit[PricingRuleKeys.MIN_RANGE]!) {
      return;
    }

    const newRules = [...rules];
    adjustRuleRanges(newRules, editIndex, ruleToEdit[PricingRuleKeys.MIN_RANGE]);
    setFieldValue(fieldAccessor, newRules);
    setEditIndex(null);
  };

  const addNewPricingRule = (isLast: boolean) => {
    validateForm().then(() => {
      if (errors) {
        for (let index = 0; index < errors.length; index++) {
          const element = errors[index];
          if (element && element[PricingRuleKeys.PRICE]) {
            const priceKey = `${fieldAccessor}[${index}].${PricingRuleKeys.PRICE}`;
            const maxKey = `${fieldAccessor}[${index}].${PricingRuleKeys.MAX_RANGE}`;

            setFieldTouched(priceKey, true);
            setFieldTouched(maxKey, true);
          }
        }
      } else {
        if (!rules || rules.length === 0) {
          return;
        }
        const lastRule = rules[rules.length - 1];
        if (!lastRule || !lastRule[PricingRuleKeys.MAX_RANGE]) {
          return;
        }

        const newRules = [
          ...rules,
          { [PricingRuleKeys.MIN_RANGE]: lastRule[PricingRuleKeys.MAX_RANGE]! + 1, isLast: isLast },
        ];
        setFieldValue(fieldAccessor, newRules);
      }
    });
  };

  const onAddLast = () => {
    addNewPricingRule(true);
    const lastIndex = rules.length - 1;

    setFieldValue(`${fieldAccessor}[${lastIndex}].${PricingRuleKeys.PRICE}`, "");
    setFieldTouched(`${fieldAccessor}[${lastIndex}].${PricingRuleKeys.PRICE}`, false);
  };

  const onDeleteLast = () => {
    deleteRule(rules.length - 1);
  };

  return (
    <>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 3fr 3fr 2fr",
          rowGap: "0.5rem",
        }}
      >
        <FieldArray
          name={fieldAccessor}
          render={arrayHelpers =>
            rules.map((rule, index) => {
              if (rule.isLast) {
                return null;
              }

              return (
                <>
                  <div>
                    <Flex height="100%" align="center" justify="flex-end">
                      <Typography>{rule[PricingRuleKeys.MIN_RANGE]}</Typography> -
                    </Flex>
                  </div>
                  <div>
                    <Flex justify="flex-start">
                      <div style={{ width: "50%" }}>
                        <FormikTextInput
                          height={"80%"}
                          noMargin
                          name={`${fieldAccessor}[${index}].${PricingRuleKeys.MAX_RANGE}`}
                          type="number"
                          disabled={index < rules.length - 1 && index !== editIndex}
                        />
                      </div>
                      {licenceType === LicenceType.CONCURRENT ? "users" : "requests"}
                    </Flex>
                  </div>
                  <div>
                    <Flex justify="flex-start">
                      £
                      <div style={{ width: "50%" }}>
                        <FormikTextInput
                          height={"80%"}
                          noMargin
                          name={`${fieldAccessor}[${index}].${PricingRuleKeys.PRICE}`}
                          type="number"
                          disabled={index < rules.length - 1 && index !== editIndex}
                        />
                      </div>
                      per {licenceType === LicenceType.CONCURRENT ? "user" : "request"}
                    </Flex>
                  </div>
                  <div>
                    <Flex height="100%" align="center">
                      <PricingRulesActionButtons
                        index={index}
                        allowEdit={index !== rules.length - 1}
                        editMode={index === editIndex}
                        onDelete={deleteRule}
                        onEditSelect={setEditIndex}
                        onEditComplete={onEditComplete}
                      />
                    </Flex>
                  </div>
                </>
              );
            })
          }
        />
      </div>
      {rules.some(item => item.isLast) && (
        <>
          <Flex margin="0.5rem 0" borderTop="1px solid rgb(158, 158, 158)"></Flex>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 3fr 3fr 2fr",
              rowGap: "0.5rem",
            }}
          >
            <Flex justify="flex-end">{rules[rules.length - 1][PricingRuleKeys.MIN_RANGE]}</Flex>
            <Flex padding="0 0 0 0.5rem" justify="flex-start">
              or more
            </Flex>
            <Flex justify="flex-start">
              £
              <div style={{ width: "50%" }}>
                <FormikTextInput
                  height={"80%"}
                  noMargin
                  name={`${fieldAccessor}[${rules.length - 1}].${PricingRuleKeys.PRICE}`}
                  type="number"
                />
              </div>
              per {licenceType === LicenceType.CONCURRENT ? "user" : "request"}
            </Flex>
            <IconButton color={theme.colors.grey[700]} hoverColor={theme.colors.error.main} onClick={onDeleteLast}>
              <IconTrash />
            </IconButton>
          </div>
          <Flex margin="0.5rem 0" borderTop="1px solid rgb(158, 158, 158)"></Flex>
        </>
      )}
      {!rules.some(item => item.isLast) && (
        <Flex width="100%" justify="flex-end">
          <Button onClick={() => addNewPricingRule(false)}>Add</Button>
          <Button onClick={onAddLast}>Add Last</Button>
        </Flex>
      )}
    </>
  );
};

export default PricingRulesRangeForm;
